import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { LogOut, AlertTriangle } from "lucide-react";

/**
 * Global red banner shown across the app when a super-admin is impersonating a user.
 * Renders nothing unless `user.impersonating === true`.
 */
export default function ImpersonationBanner() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (user && user.impersonating) {
      document.body.style.paddingTop = "44px";
      return () => { document.body.style.paddingTop = ""; };
    }
  }, [user]);

  if (!user || !user.impersonating) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      await api.post("/admin/end-impersonation");
      if (refreshUser) await refreshUser();
      navigate("/admin");
    } catch (e) {
      console.error(e);
    } finally {
      setEnding(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-[#7A1A1A] via-[#A22] to-[#7A1A1A] text-white" data-testid="impersonation-banner">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <AlertTriangle size={14} className="flex-shrink-0" />
          <span className="text-[12px] font-bold uppercase tracking-[0.15em]">Admin Impersonation Mode</span>
          <span className="text-[11px] text-white/85 truncate">— viewing as <strong>{user.email}</strong></span>
        </div>
        <button
          onClick={handleEnd}
          disabled={ending}
          className="bg-white text-[#7A1A1A] font-bold uppercase tracking-wider text-[10px] px-3 py-1.5 rounded-sm hover:bg-white/90 active:scale-[0.98] transition-all inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="impersonation-end-btn"
        >
          <LogOut size={11} /> {ending ? "Exiting..." : "Exit Impersonation"}
        </button>
      </div>
    </div>
  );
}
